import { Component, Prop, State, Event, h, Listen, Element, type EventEmitter } from '@stencil/core';
import { ConversationalSearchFeedback } from '../../conversational-search-partials/feedback/feedback';
import { ConversationalSearchInputErrorMessage } from '../../conversational-search-partials/input-error-message/input-error-message';
import { isMobile } from '../../../../../utils/media';
import { keyPressHandler, modifyInputValue, scrollHandler, sendClickHandler } from './chat-widget-body-helpers';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';

@Component({
  tag: 'uiaas-conversational-search-chat-widget-body',
  styleUrl: 'chat-widget-body.scss',
  shadow: true,
})
export class WidgetConversationalSearchChatWidgetBody {
  @Prop() readonly labelProvider: labelProviderInterface;
  @Prop() readonly language: string;
  @Prop() readonly country: string;
  @Prop() readonly searchQuery: string; // Guided Search/URL query
  @Prop() readonly apigeeAuth: string;
  @Prop() readonly apigeeTokenUrl: string;
  @Prop() readonly conversationalSearchProductsEndpoint: string;
  @Prop() readonly conversationalSearchMedaliaFormId: string;
  @Prop() readonly conversationalSearchBrandUrl: string;
  @Prop() readonly brand?: string;

  @State() isProcessing = false;
  @State() isInputErrorVisible = false;
  @State() chatQuery: string; // Query from Gen_AI textarea
  @State() chatQueryIndex = 0; // Isolating chatQuery value change from new message state change
  @State() isMobileView = isMobile();
  @State() targetQuery: string;

  @Element() el: HTMLElement;

  private scrollTimeout: number;
  private preloadQuery: string; // Saving for initial preload all result, before accepting terms and conditions

  @Listen('resize', {target: 'window'})
  @Listen('load', {target: 'window'})
  handleResize() {
      this.isMobileView = isMobile();
  }

  @Listen('conversational-search:user-start-of-conversation', {
    target: 'window',
  })
  acceptingTermstHandler() {
      if (this.preloadQuery) this.chatQuery = this.preloadQuery;
  }

  @Listen('conversational-search:user-new-conversation', { target: 'window' })
  @Listen('conversational-search:user-end-of-conversation', { target: 'window' })
  conversationStateHandler() {
      this.chatQuery = '';
      modifyInputValue(this.el);
      this.isInputErrorVisible = false;
      this.focusTextArea();
  }

  @Listen('conversational-search:select-characteristics', { target: 'window' })
  handleSelectedCharacteristics(e) {
      this.isInputErrorVisible = false;
      modifyInputValue(this.el, e.detail.characteristics);
  }

  @Listen('conversational-search:invoke-scrollbar', { target: 'window' })
  scrollbarInvocationhandler() {
      scrollHandler(this.el, this.scrollTimeout);
  }

  @Listen('conversational-search:user-question-state', { target: 'window' })
  handleProcessingChange(e) {
      this.isProcessing = e.detail.isProcessing;
  }

  @Listen('conversational-search:input-action', { target: 'window' })
  handleNewchatQueryFromBanner(e) {
      this.preloadQuery = e.detail.query;
      this.chatQuery = e.detail.query;
      this.chatQueryIndex++;
  }

  @Listen('conversational-search:predefined-question', { target: 'window' })
  handleNewchatQueryFromSuggestions(e) {
      this.chatQuery = e.detail.query;
      this.chatQueryIndex++;
  }

  @Listen('conversational-search:messages-update', { target: 'window' })
  handleMessagesChange() {
      this.isInputErrorVisible = false;
  }

  @Event({
    eventName: 'conversational-search:user-query',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    chatQueryEventEmitter: EventEmitter<{ query: string }>;

  private focusTextArea() {
      (this.el.shadowRoot.querySelector('qds-input') as HTMLInputElement).focus();
  }

  disconnectedCallback() {
      if (this.scrollTimeout) {
        window.clearTimeout(this.scrollTimeout);
      }
  }

  render() {
      this.targetQuery = this.chatQuery !== '' ? this.chatQuery : this.searchQuery;
      const isTextAreaDisabled = this.isProcessing;
      const tooltipText = this.labelProvider(
        'conversational.search.textarea.tooltip',
        'Modify your selection or add another option, then click Enter to see updated results'
      );

      const outroText = this.labelProvider(
        'conversational.search.banner.dynamic.data.result.outro.disclaimer.v2',
        'This feature is powered by Artificial Intelligence and generates automated responses that may not always be accurate.',
      );

      const outroTtooltipText = this.labelProvider(
        'conversational.search.banner.dynamic.data.result.outro.disclaimer.tooltip',
        'This feature is powered by Artificial Intelligence and generates automated responses that may not always be accurate, complete or updated. Do not rely solely on this information for decision-making. By using it, you acknowledge interacting with AI and agree with our {0} and {1}',
      );

      const termsOfUse = this.labelProvider('conversational.search.banner.dynamic.data.result.outro.disclaimer.tooltip.termsOfUse', 'Terms of Use');
      const privacyPolicy = this.labelProvider('conversational.search.banner.dynamic.data.result.outro.disclaimer.tooltip.privacyPolicy', 'Privacy Policy');
      const outroTooltipHtmlText = outroTtooltipText?.replace('{0}', `<span class='tooltip-links'>${termsOfUse}</span>`)?.replace('{1}', `<span class='tooltip-links'>${privacyPolicy}</span>`);

      return (
        <div class='conversational-search-chat-widget-body'>
          <uiaas-search-conversational-search-mobile-header
            labelProvider={(e, f) => this.labelProvider(e, f)}
            query={this.targetQuery}
            country={this.country}
            language={this.language}
          />
          <section class="conversational-search-chat-widget-body-elements">
            <uiaas-search-widget-conversational-search-back-button
              query={this.targetQuery}
              labelProvider={(e, f) => this.labelProvider(e, f)}
            />
            <uiaas-search-widget-conversational-search-messages-mediators-wrapper
              searchQuery={this.searchQuery}
              chatQuery={this.chatQuery}
              chatQueryIndex={this.chatQueryIndex}
              labelProvider={(e, f) => this.labelProvider(e, f)}
              country={this.country}
              language={this.language}
              conversationalSearchBrandUrl={this.conversationalSearchBrandUrl}
              apigeeAuth={this.apigeeAuth}
              apigeeTokenUrl={this.apigeeTokenUrl}
              conversationalSearchProductsEndpoint={this.conversationalSearchProductsEndpoint}
              conversationalSearchMedaliaFormId={this.conversationalSearchMedaliaFormId}
              brand={this.brand}
            />
          </section>
          <section class="conversational-search-chat-widget-body-control">
            <uiaas-search-conversational-search-action-messages
              labelProvider={(e, f) => this.labelProvider(e, f)}
              conversationalSearchMedaliaFormId={this.conversationalSearchMedaliaFormId}
              country={this.country}
              language={this.language}
              query={this.targetQuery}
              focusCallback={() => this.focusTextArea()}
            />
            <div class="conversational-search-chat-widget-body-control-wrapper">
              <uiaas-search-conversational-search-tooltip-reactivity-wrapper text={tooltipText} />
              <div class='conversational-search-chat-widget-body-control-wrapper-actions'>
                <div class="qds-control-group-inline conversational-search-chat-widget-body-control-wrapper-actions-container">
                  <qds-input
                    size="large"
                    placeholder={this.labelProvider(
                      'conversational.search.chat.textarea.input',
                      'Ask a product follow up question...',
                    )}
                    onKeyDown={(e) => keyPressHandler(this, e)}
                    inputmode="text"
                    type="text"
                    disabled={isTextAreaDisabled}
                    required={this.isInputErrorVisible}
                  />
                  <qds-button
                    importance="standard"
                    disabled={isTextAreaDisabled}
                    icon-name="paper-airplane"
                    onClick={() => sendClickHandler(this, isTextAreaDisabled)}
                    size="large"
                    iconOnly={true}
                  />  
                </div>
                <uiaas-search-conversational-search-new-conversation-cta
                  labelProvider={(e, f) => this.labelProvider(e, f)}
                  isTextAreaDisabled={isTextAreaDisabled}
                  query={this.targetQuery}
                />
              </div>
              {this.isInputErrorVisible ? (
                <ConversationalSearchInputErrorMessage
                  labelProvider={(e, f) => this.labelProvider(e, f)}
                />
              ) : (
                ''
              )}
              <ConversationalSearchFeedback
                text={outroText}
                tooltipText={outroTooltipHtmlText}
              />
            </div>
          </section>
        </div>
      );
  }
}
